/**
 * Server envelope mirror (server/src/platform/errors/NovanalyseError.js).
 *   success: { success: true, data, ...extra }
 *   failure: { success: false, code, message, details? }
 */

export interface SuccessEnvelope<T> {
  success: true;
  data: T;
  [extra: string]: unknown;
}

export interface FailureEnvelope {
  success: false;
  code: string;
  message: string;
  details?: unknown;
}

export type Envelope<T> = SuccessEnvelope<T> | FailureEnvelope;

/** Stable machine codes the client branches on (mirror of server ERROR_CODES). */
export const ERROR_CODES = {
  INTERNAL: "INTERNAL",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION: "VALIDATION",
  RATE_LIMITED: "RATE_LIMITED",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  ACCOUNT_INACTIVE: "ACCOUNT_INACTIVE",
  UNAUTHENTICATED: "UNAUTHENTICATED",
  FORBIDDEN: "FORBIDDEN",
  LABORATOIRE_SCOPE_FORBIDDEN: "LABORATOIRE_SCOPE_FORBIDDEN",
  SESSION_EXPIRED: "SESSION_EXPIRED",
  CONFIG_INVALID: "CONFIG_INVALID",
  MANIFEST_INVALID: "MANIFEST_INVALID",
  NOT_IMPLEMENTED: "NOT_IMPLEMENTED",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** The normalized client-side error every query/mutation throws on `{ success: false }`. */
export class ApiError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  readonly details?: unknown;

  constructor(code: string, message: string, httpStatus: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.httpStatus = httpStatus;
    if (details !== undefined) this.details = details;
  }

  /** True when the server says auth/session is gone — the auth layer reacts to this. */
  get isUnauthenticated(): boolean {
    return (
      this.httpStatus === 401 ||
      this.code === ERROR_CODES.UNAUTHENTICATED ||
      this.code === ERROR_CODES.SESSION_EXPIRED
    );
  }

  get isForbidden(): boolean {
    return (
      this.httpStatus === 403 ||
      this.code === ERROR_CODES.FORBIDDEN ||
      this.code === ERROR_CODES.LABORATOIRE_SCOPE_FORBIDDEN
    );
  }
}

/** Auth token pair (mirror of POST /auth/login | /auth/refresh response data). */
export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: "Bearer";
  expires_in: number;
}
